import { useState } from 'react';

import { CreateGroupForm } from '../groups/CreateGroupForm.tsx';
import { GroupSettings } from '../groups/GroupSettings.tsx';
import { GroupSwitcher } from '../groups/GroupSwitcher.tsx';
import { NewStoreForm } from '../items/NewStoreForm.tsx';
import { StoreSettings } from '../items/StoreSettings.tsx';
import { canInvite } from '../groups/groupsApi.ts';
import { useGroups, useSelectedGroup } from '../groups/useGroups.ts';
import { InstallBanner } from '../install/InstallBanner.tsx';
import { InviteMemberForm } from '../invitations/InviteMemberForm.tsx';
import { PendingInvitations } from '../invitations/PendingInvitations.tsx';
import { useLists } from '../items/useLists.ts';
import { Modal } from '../../ui/Modal.tsx';
import { AppMenu } from './AppMenu.tsx';
import { GroupView } from './GroupView.tsx';

export function HomePage() {
  const { data: groups, isPending, isError } = useGroups();
  const { selected, select } = useSelectedGroup(groups);

  // Opened from the menu here, rendered by GroupView: the lists panel acts on whichever
  // list the switcher is showing, and that selection lives down there. One boolean
  // travelling down beats hoisting it up.
  const [isListsOpen, setIsListsOpen] = useState(false);

  // Inviting and the group panel need the group and nothing else, which is already here,
  // so they are rendered here.
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isGroupsOpen, setIsGroupsOpen] = useState(false);

  // Only for the menu's hint. The *selection* is not readable from here by design, so the
  // entry names the list when there is one and counts them when there are more — both
  // answerable from the query alone, with no second copy of what is showing.
  const { data: lists } = useLists(selected?.id);

  return (
    <main className="app-shell">
      <header className="app-header">
        {selected && groups ? (
          <GroupSwitcher groups={groups} selected={selected} onSelect={select} />
        ) : (
          <h1 className="group-title">Listocial</h1>
        )}

        <AppMenu
          onOpenInvite={selected && canInvite(selected) ? () => setIsInviteOpen(true) : undefined}
          onOpenLists={selected ? () => setIsListsOpen(true) : undefined}
          listsHint={describeLists(lists?.map((list) => list.name))}
          onOpenGroups={selected ? () => setIsGroupsOpen(true) : undefined}
          groupsHint={selected?.name}
        />
      </header>

      {isPending && <p className="muted">Loading…</p>}

      {isError && (
        <p className="form-error" role="alert">
          Could not load your groups. Check your connection and try again.
        </p>
      )}

      {/* Above everything: an invitation is time-sensitive and easily missed. */}
      <PendingInvitations />

      {groups?.length === 0 && (
        <CreateGroupForm
          heading="Start a list"
          description="A group is the household, couple or flatshare you shop for. You can invite the others once it exists."
          submitLabel="Create"
        />
      )}

      {/* Unconditional, because taking the browser's own offer away and then not making one
          leaves the person with nothing. `startInstallOffer` calls `preventDefault()` during
          page load, long before the groups query has answered, so a `selected &&` guard here
          silently cancelled an install we had already suppressed — which is exactly what
          happened to somebody who was still holding a pending invitation when Chrome fired.
          The store is the only thing that decides whether there is anything to offer.

          Placed below the create-group form rather than above it so the empty screen still
          leads with its one job, and above the list so it reads the same as it always did
          for everyone who has a group. */}
      <InstallBanner />

      {/* Keyed by group so switching household resets everything below it rather than
          carrying one group's state into another. */}
      {selected && (
        <GroupView
          key={selected.id}
          group={selected}
          isListsOpen={isListsOpen}
          onCloseLists={() => setIsListsOpen(false)}
        />
      )}

      {isInviteOpen && selected && (
        <Modal title="Invite someone" onClose={() => setIsInviteOpen(false)}>
          <InviteMemberForm group={selected} />
        </Modal>
      )}

      {isGroupsOpen && selected && (
        <Modal title="Groups" onClose={() => setIsGroupsOpen(false)}>
          <div className="settings">
            <GroupSettings group={selected} />

            {/* Items components in the Groups panel, because the shops a household walks
                are a fact about the household rather than about a list. Rendered here for
                the reason every other panel is rendered where it is: this is where the
                group already is. Settings then creation, in that order, exactly as the
                Lists panel pairs ListSettings with NewListForm. */}
            <StoreSettings groupId={selected.id} />

            <NewStoreForm groupId={selected.id} />

            <CreateGroupForm
              compact
              heading="New group"
              description="A separate household or flatshare, with its own lists and people."
              submitLabel="Create"
            />
          </div>
        </Modal>
      )}
    </main>
  );
}

function describeLists(names: string[] | undefined): string | undefined {
  if (!names || names.length === 0) {
    return undefined;
  }

  return names.length === 1 ? names[0] : `${names.length} lists`;
}
